import {
  ChangeDetectionStrategy,
  Component, ElementRef,
  EventEmitter,
  inject, Input,
  Output,
  QueryList,
  signal, ViewChild,
  ViewChildren
} from '@angular/core';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { TableUtils } from '../../../../core/utils/TableUtils';
import { SearchComponent } from '../../../../shared/components/search/search.component';
import { CustomersService } from '../../core/services/CustomersService';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { ICustomer } from '../../core/interfaces/ICustomer';
import { AddCustomerModalComponent } from '../add-customer-modal/add-customer-modal.component';
import { EditCustomerModalComponent } from '../edit-customer-modal/edit-customer-modal.component';
import { CartStore } from '../../../cart/core/stores/CartStore';

export interface CustomersSearchModalOpenData {
  customerId?: number;
  skipCartCustomerPreload?: boolean;
}

@Component({
  selector: 'app-customers-search-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    LoaderComponent,
    SearchComponent,
    AddCustomerModalComponent,
    EditCustomerModalComponent,
  ],
  templateUrl: './customers-search-modal.component.html',
  styleUrl: './customers-search-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomersSearchModalComponent extends BaseModalComponent<CustomersSearchModalOpenData> {

  @ViewChildren('trEl') trElList: QueryList<ElementRef<HTMLTableRowElement>>;
  @ViewChild(SearchComponent) searchComponent: SearchComponent;

  @Input({ required: true }) showAnonymButton: boolean;
  @Input() openDetailsOnRowClick = false;

  @Output() confirmCustomerSelection = new EventEmitter<ICustomer>();
  @Output() dismissed = new EventEmitter<void>();

  public searchVal = '';
  public selectedCustomer: ICustomer;
  public customers: ICustomer[] = [];
  public readonly isLoading = signal<boolean>(false);

  private readonly _customersService = inject(CustomersService);
  private readonly _cartStore = inject(CartStore);

  public override open(val?: CustomersSearchModalOpenData): void {
    this.customers = null;
    this.selectedCustomer = null;
    super.open(val);

    let customerId = val?.customerId;

    if (customerId == null && !val?.skipCartCustomerPreload) {
      customerId = this._cartStore.cart().customerId;
    }

    if (customerId) {
      this.isLoading.set(true);

      this._customersService.getCustomerById(customerId)
        .pipe(
          finalize(() => this.isLoading.set(false)),
        )
        .subscribe({
          next: res => {
            this.customers = [res.data];
            this.selectedCustomer = res.data;
            this.cdr.detectChanges();
          }
        });
    }
  }

  public selectCustomerRow(customer: ICustomer, trEl: HTMLTableRowElement): void {
    if (this.openDetailsOnRowClick) {
      this.confirmCustomerSelection.emit(customer);
      this.close();

      return;
    }

    if (this.selectedCustomer?.id === customer.id) {
      this.selectedCustomer = null;
    } else {
      this.selectedCustomer = customer;
    }

    this.toggleRow(trEl);
  }

  public handleCustomersSearchChange(): void {
    this.customers = null;
    this.selectedCustomer = null;

    if (this.searchVal !== '') {
      this.isLoading.set(true);

      this._customersService.getCustomers(this.searchVal)
        .pipe(
          finalize(() => this.isLoading.set(false)),
        )
        .subscribe({
          next: res => this.customers = res.data,
          error: ({ error }: ApiErrorResponse) => {
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`get-customers/notifications/${error.message}.Message`)
            );
          }
        });
    }
  }

  public toggleRow(trEl: HTMLTableRowElement, forceOpen = false): void {
    TableUtils.toggleExpandableRow(this.trElList, trEl, forceOpen);
  }

  public updateCustomerInList(customer: ICustomer): void {
    const fCustomer = this.customers.find(c => c.id === customer.id);

    if (fCustomer) {
      Object.assign(fCustomer, customer);
      this.cdr.detectChanges();
    }
  }
}
